import { useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose, faPlus, faSave, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import PromotoraService from '../../../services/promotoraService';
import IntegracaoService from '../../../services/integracaoService';
import { PromotoraDTO } from '../../../shared/dto/promotoraDTO';
import { PromotoraDadosGestaoDTO } from '../../../shared/dto/promotoraDadosGestaoDTO';
import Spinner from '../../../shared/spinner';

interface Props {
  show: boolean;
  promotora: PromotoraDTO | null;
  obrigatorio?: boolean;
  onHide: () => void;
  onSaved?: () => void;
}

type Socio = { nome: string; cpfCnpj: string; experiencia: string; percentual: string; telefone: string; email?: string; assinaContrato?: boolean };
type Diretor = { nome: string; cpfCnpj: string; cargo: string; telefone: string; email?: string; assinaContrato?: boolean };
type Testemunha = { nome: string; cpfCnpj: string; telefone: string; email?: string; enviaDocsign?: boolean };
type Relacionamento = { nome: string; tempo: string };

const socioVazio = (): Socio => ({ nome: '', cpfCnpj: '', experiencia: '', percentual: '', telefone: '', email: '', assinaContrato: false });
const diretorVazio = (): Diretor => ({ nome: '', cpfCnpj: '', cargo: '', telefone: '', email: '', assinaContrato: false });
const testemunhaVazia = (): Testemunha => ({ nome: '', cpfCnpj: '', telefone: '', email: '', enviaDocsign: false });
const relacionamentoVazio = (): Relacionamento => ({ nome: '', tempo: '' });
const sociosVazios = (): Socio[] => [
  socioVazio()
];
const diretoresVazios = (): Diretor[] => [
  diretorVazio()
];
const testemunhasVazias = (): Testemunha[] => [
  testemunhaVazia()
];

const relacionamentosVazios = (): Relacionamento[] => [
  relacionamentoVazio()
];

const parseLista = <T,>(valor: string | undefined, fallback: T[]): T[] => {
  if (!valor) return fallback;
  try {
    const parsed = JSON.parse(valor);
    if (!Array.isArray(parsed)) return fallback;
    const itensPreenchidos = parsed.filter((item) => item
      && Object.values(item).some((campo) => valorPreenchido(campo)));
    return itensPreenchidos.length > 0 ? itensPreenchidos as T[] : fallback;
  } catch {
    return fallback;
  }
};

const valorPreenchido = (valor: any) => {
  if (typeof valor === 'boolean') return valor;
  return Boolean(String(valor || '').trim());
};

function PromotoraDadosGestaoModal({ show, promotora, obrigatorio = false, onHide, onSaved }: Props) {
  const promotoraService = new PromotoraService();
  const integracaoService = new IntegracaoService();
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'warning' | 'danger'; text: string } | null>(null);
  const [docSignAtivo, setDocSignAtivo] = useState(false);

  const [socios, setSocios] = useState<Socio[]>(sociosVazios());
  const [diretores, setDiretores] = useState<Diretor[]>(diretoresVazios());
  const [testemunhas, setTestemunhas] = useState<Testemunha[]>(testemunhasVazias());
  const [atuacaoGeografica, setAtuacaoGeografica] = useState('');
  const [quantidadePostosAtendimento, setQuantidadePostosAtendimento] = useState('');
  const [descricaoPostosAtendimento, setDescricaoPostosAtendimento] = useState('');
  const [conveniosAtivos, setConveniosAtivos] = useState<Relacionamento[]>(relacionamentosVazios());
  const [parceiros, setParceiros] = useState<Relacionamento[]>(relacionamentosVazios());
  const [tempoOperacao, setTempoOperacao] = useState('');
  const [produtoConsignadoPublico, setProdutoConsignadoPublico] = useState(false);
  const [produtoConsignadoPrivado, setProdutoConsignadoPrivado] = useState(false);
  const [produtoInss, setProdutoInss] = useState(false);
  const [produtoEmprestimoPessoal, setProdutoEmprestimoPessoal] = useState(false);
  const [nomeBanco, setNomeBanco] = useState('');
  const [numeroBanco, setNumeroBanco] = useState('');
  const [numeroAgencia, setNumeroAgencia] = useState('');
  const [contaCorrente, setContaCorrente] = useState('');

  const atualizarSocio = (index: number, campo: keyof Socio, valor: string | boolean) => {
    setSocios((prev) => prev.map((item, i) => i === index ? { ...item, [campo]: valor } : item));
  };

  const adicionarSocio = () => {
    setSocios((prev) => [...prev, socioVazio()]);
  };

  const removerSocio = (index: number) => {
    setSocios((prev) => prev.length === 1 ? prev : prev.filter((_, i) => i !== index));
  };

  const atualizarDiretor = (index: number, campo: keyof Diretor, valor: string | boolean) => {
    setDiretores((prev) => prev.map((item, i) => i === index ? { ...item, [campo]: valor } : item));
  };

  const adicionarDiretor = () => {
    setDiretores((prev) => [...prev, diretorVazio()]);
  };

  const removerDiretor = (index: number) => {
    setDiretores((prev) => prev.length === 1 ? prev : prev.filter((_, i) => i !== index));
  };

  const atualizarTestemunha = (index: number, campo: keyof Testemunha, valor: string | boolean) => {
    setTestemunhas((prev) => prev.map((item, i) => i === index ? { ...item, [campo]: valor } : item));
  };

  const adicionarTestemunha = () => {
    setTestemunhas((prev) => [...prev, testemunhaVazia()]);
  };

  const removerTestemunha = (index: number) => {
    setTestemunhas((prev) => prev.length === 1 ? prev : prev.filter((_, i) => i !== index));
  };

  const atualizarRelacionamento = (
    lista: Relacionamento[],
    setLista: (valor: Relacionamento[]) => void,
    index: number,
    campo: keyof Relacionamento,
    valor: string
  ) => {
    setLista(lista.map((item, i) => i === index ? { ...item, [campo]: valor } : item));
  };

  const adicionarRelacionamento = (
    lista: Relacionamento[],
    setLista: (valor: Relacionamento[]) => void
  ) => {
    setLista([...lista, relacionamentoVazio()]);
  };

  const removerRelacionamento = (
    lista: Relacionamento[],
    setLista: (valor: Relacionamento[]) => void,
    index: number
  ) => {
    if (lista.length === 1) return;
    setLista(lista.filter((_, i) => i !== index));
  };

  const carregarDados = async () => {
    if (!promotora?.id) return;
    setLoading(true);
    setAlert(null);
    try {
      const response = await promotoraService.buscarDadosGestaoPromotora(promotora.id);
      const dados = response.data || {};
      setSocios(parseLista<Socio>(dados.sociosJson, sociosVazios()).map((socio) => ({
        ...socioVazio(),
        ...socio,
        email: socio.email || '',
        assinaContrato: Boolean(socio.assinaContrato || (socio as any).assina_contrato)
      })));
      setDiretores(parseLista<Diretor>(dados.diretoresJson, diretoresVazios()).map((diretor) => ({
        ...diretorVazio(),
        ...diretor,
        email: diretor.email || '',
        assinaContrato: Boolean(diretor.assinaContrato || (diretor as any).assina_contrato)
      })));
      setTestemunhas(parseLista<Testemunha>(dados.testemunhasJson, testemunhasVazias()).map((testemunha) => ({
        ...testemunhaVazia(),
        ...testemunha,
        email: testemunha.email || '',
        enviaDocsign: Boolean(testemunha.enviaDocsign || (testemunha as any).envia_docsign
          || (testemunha as any).enviarDocsign || (testemunha as any).enviar_para_docsign
          || (testemunha as any).enviarParaDocsign)
      })));
      setAtuacaoGeografica(dados.atuacaoGeografica || '');
      setQuantidadePostosAtendimento(dados.quantidadePostosAtendimento || '');
      setDescricaoPostosAtendimento(dados.descricaoPostosAtendimento || '');
      setConveniosAtivos(parseLista<Relacionamento>(dados.conveniosAtivosJson, relacionamentosVazios()));
      setParceiros(parseLista<Relacionamento>(dados.parceirosJson, relacionamentosVazios()));
      setTempoOperacao(dados.tempoOperacao || '');
      setProdutoConsignadoPublico(!!dados.produtoConsignadoPublico);
      setProdutoConsignadoPrivado(!!dados.produtoConsignadoPrivado);
      setProdutoInss(!!dados.produtoInss);
      setProdutoEmprestimoPessoal(!!dados.produtoEmprestimoPessoal);
      setNomeBanco(dados.nomeBanco || promotora.nomeBanco || '');
      setNumeroBanco(dados.numeroBanco || promotora.codigoBanco || '');
      setNumeroAgencia(dados.numeroAgencia || promotora.agencia || '');
      setContaCorrente(dados.contaCorrente || promotora.conta || '');
    } catch (err: any) {
      setAlert({ type: 'danger', text: err.response?.data || 'Erro ao carregar dados obrigatórios.' });
    } finally {
      setLoading(false);
    }
  };

  const carregarDocSignAtivo = async () => {
    try {
      const response = await integracaoService.verificarIntegracaoAtiva('DOCSIGN');
      setDocSignAtivo(Boolean(response.data));
    } catch {
      setDocSignAtivo(false);
    }
  };

  const validar = () => {
    const socioPrincipal = socios[0];
    const convenioPrincipal = conveniosAtivos.find((item) => item.nome.trim() && item.tempo.trim());
    const parceiroPrincipal = parceiros.find((item) => item.nome.trim() && item.tempo.trim());
    if (!socioPrincipal.nome.trim() || !socioPrincipal.cpfCnpj.trim() || !socioPrincipal.experiencia.trim()
      || !socioPrincipal.percentual.trim() || !socioPrincipal.telefone.trim()) return false;
    const signatarioInvalido = [...socios, ...diretores].some((pessoa) => Boolean(pessoa.assinaContrato)
      && (!String(pessoa.nome || '').trim() || !String(pessoa.email || '').trim()));
    const testemunhaDocSignInvalida = testemunhas.some((pessoa) => Boolean(pessoa.enviaDocsign)
      && (!String(pessoa.nome || '').trim() || !String(pessoa.email || '').trim()));
    if (docSignAtivo && (signatarioInvalido || testemunhaDocSignInvalida)) return false;
    if (!atuacaoGeografica.trim() || !quantidadePostosAtendimento.trim() || !descricaoPostosAtendimento.trim()) return false;
    if (!convenioPrincipal || !parceiroPrincipal || !tempoOperacao.trim()) return false;
    if (!produtoConsignadoPublico && !produtoConsignadoPrivado && !produtoInss && !produtoEmprestimoPessoal) return false;
    if (!nomeBanco.trim() || !numeroBanco.trim() || !numeroAgencia.trim() || !contaCorrente.trim()) return false;
    return true;
  };

  const salvar = async () => {
    if (!promotora?.id) return;
    if (!validar()) {
      setAlert({ type: 'warning', text: 'Preencha todos os dados obrigatórios da gestão.' });
      return;
    }

    const dto = new PromotoraDadosGestaoDTO();
    dto.idPromotora = Number(promotora.id);
    const sociosNormalizados = socios
      .map((item) => ({ ...item, email: docSignAtivo ? String(item.email || '').trim() : '', assinaContrato: docSignAtivo && Boolean(item.assinaContrato) }))
      .filter((item) => Object.values(item).some((valor) => valorPreenchido(valor)));
    const diretoresNormalizados = diretores
      .map((item) => ({ ...item, email: docSignAtivo ? String(item.email || '').trim() : '', assinaContrato: docSignAtivo && Boolean(item.assinaContrato) }))
      .filter((item) => Object.values(item).some((valor) => valorPreenchido(valor)));
    const testemunhasNormalizadas = testemunhas
      .map((item) => ({ ...item, email: docSignAtivo ? String(item.email || '').trim() : '', enviaDocsign: docSignAtivo && Boolean(item.enviaDocsign) }))
      .filter((item) => Object.values(item).some((valor) => valorPreenchido(valor)));
    dto.sociosJson = JSON.stringify(sociosNormalizados);
    dto.diretoresJson = JSON.stringify(diretoresNormalizados);
    dto.testemunhasJson = JSON.stringify(testemunhasNormalizadas);
    dto.atuacaoGeografica = atuacaoGeografica.trim();
    dto.quantidadePostosAtendimento = quantidadePostosAtendimento.trim();
    dto.descricaoPostosAtendimento = descricaoPostosAtendimento.trim();
    dto.conveniosAtivosJson = JSON.stringify(conveniosAtivos.filter((item) => item.nome.trim() || item.tempo.trim()));
    dto.parceirosJson = JSON.stringify(parceiros.filter((item) => item.nome.trim() || item.tempo.trim()));
    dto.tempoOperacao = tempoOperacao.trim();
    dto.produtoConsignadoPublico = produtoConsignadoPublico;
    dto.produtoConsignadoPrivado = produtoConsignadoPrivado;
    dto.produtoInss = produtoInss;
    dto.produtoEmprestimoPessoal = produtoEmprestimoPessoal;
    dto.nomeBanco = nomeBanco.trim();
    dto.numeroBanco = numeroBanco.trim();
    dto.numeroAgencia = numeroAgencia.trim();
    dto.contaCorrente = contaCorrente.trim();

    setLoading(true);
    try {
      await promotoraService.salvarDadosGestaoPromotora(dto);
      setAlert({ type: 'success', text: 'Dados obrigatórios salvos com sucesso.' });
      onSaved?.();
    } catch (err: any) {
      setAlert({ type: 'danger', text: err.response?.data || 'Erro ao salvar dados obrigatórios.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      carregarDocSignAtivo();
      carregarDados();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, promotora?.id]);

  return (
    <Modal show={show} onHide={obrigatorio ? undefined : onHide} fullscreen backdrop={obrigatorio ? 'static' : true} keyboard={!obrigatorio}>
      <Modal.Header closeButton={!obrigatorio}>
        <Modal.Title>Dados obrigatórios da gestão</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: 'calc(100vh - 125px)', overflowY: 'auto' }}>
        <div className="mb-3">
          <strong>{promotora?.id} - {promotora?.nome}</strong>
        </div>
        {alert && <Alert variant={alert.type}>{alert.text}</Alert>}
        {loading && <Spinner loading={loading} />}

        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="mb-0">Dados dos sócios</h6>
          <Button size="sm" variant="outline-primary" onClick={adicionarSocio} disabled={loading}>
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Adicionar sócio
          </Button>
        </div>
        {socios.map((socio, index) => (
          <Row className="g-2 mb-2 align-items-center" key={`socio-${index}`}>
            <Col md={2}><Form.Control placeholder={`Nome do Sócio (${index + 1})`} value={socio.nome} onChange={(e) => atualizarSocio(index, 'nome', e.target.value)} /></Col>
            <Col md={2}><Form.Control placeholder="CPF/CNPJ" value={socio.cpfCnpj} onChange={(e) => atualizarSocio(index, 'cpfCnpj', e.target.value)} /></Col>
            <Col md={2}><Form.Control placeholder="Experiência profissional" value={socio.experiencia} onChange={(e) => atualizarSocio(index, 'experiencia', e.target.value)} /></Col>
            <Col md={1}><Form.Control placeholder="Participação (%)" value={socio.percentual} onChange={(e) => atualizarSocio(index, 'percentual', e.target.value)} /></Col>
            <Col md={2}><Form.Control placeholder="Telefone" value={socio.telefone} onChange={(e) => atualizarSocio(index, 'telefone', e.target.value)} /></Col>
            {docSignAtivo && (
              <Col md={2}>
                <Form.Control
                  type="email"
                  placeholder="E-mail do sócio"
                  value={socio.email || ''}
                  onChange={(e) => atualizarSocio(index, 'email', e.target.value)}
                />
                <Form.Check
                  className="mt-1"
                  type="checkbox"
                  label="Assina Contrato"
                  checked={Boolean(socio.assinaContrato)}
                  disabled={loading}
                  onChange={(e) => atualizarSocio(index, 'assinaContrato', e.target.checked)}
                />
              </Col>
            )}
            <Col md={1}>
              <Button
                size="sm"
                variant="outline-danger"
                className="w-100"
                title="Remover sócio"
                onClick={() => removerSocio(index)}
                disabled={loading || socios.length === 1}
              >
                <FontAwesomeIcon icon={faTrashAlt} />
              </Button>
            </Col>
          </Row>
        ))}

        <div className="d-flex justify-content-between align-items-center mt-3 mb-2">
          <h6 className="mb-0">Dados dos diretores</h6>
          <Button size="sm" variant="outline-primary" onClick={adicionarDiretor} disabled={loading}>
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Adicionar diretor
          </Button>
        </div>
        {diretores.map((diretor, index) => (
          <Row className="g-2 mb-2 align-items-center" key={`diretor-${index}`}>
            <Col md={docSignAtivo ? 2 : 3}>
              <Form.Control
                placeholder={`Nome do Diretor (${index + 1})`}
                value={diretor.nome}
                onChange={(e) => atualizarDiretor(index, 'nome', e.target.value)}
              />
            </Col>
            <Col md={docSignAtivo ? 2 : 3}>
              <Form.Control
                placeholder="CPF/CNPJ"
                value={diretor.cpfCnpj}
                onChange={(e) => atualizarDiretor(index, 'cpfCnpj', e.target.value)}
              />
            </Col>
            <Col md={docSignAtivo ? 2 : 3}>
              <Form.Control
                placeholder="Cargo"
                value={diretor.cargo}
                onChange={(e) => atualizarDiretor(index, 'cargo', e.target.value)}
              />
            </Col>
            <Col md={docSignAtivo ? 2 : 2}>
              <Form.Control
                placeholder="Telefone"
                value={diretor.telefone}
                onChange={(e) => atualizarDiretor(index, 'telefone', e.target.value)}
              />
            </Col>
            {docSignAtivo && (
              <Col md={3}>
                <Form.Control
                  type="email"
                  placeholder="E-mail do diretor"
                  value={diretor.email || ''}
                  onChange={(e) => atualizarDiretor(index, 'email', e.target.value)}
                />
                <Form.Check
                  className="mt-1"
                  type="checkbox"
                  label="Assina Contrato"
                  checked={Boolean(diretor.assinaContrato)}
                  disabled={loading}
                  onChange={(e) => atualizarDiretor(index, 'assinaContrato', e.target.checked)}
                />
              </Col>
            )}
            <Col md={1}>
              <Button
                size="sm"
                variant="outline-danger"
                className="w-100"
                title="Remover diretor"
                onClick={() => removerDiretor(index)}
                disabled={loading || diretores.length === 1}
              >
                <FontAwesomeIcon icon={faTrashAlt} />
              </Button>
            </Col>
          </Row>
        ))}

        <div className="d-flex justify-content-between align-items-center mt-3 mb-2">
          <h6 className="mb-0">Dados das testemunhas</h6>
          <Button size="sm" variant="outline-primary" onClick={adicionarTestemunha} disabled={loading}>
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Adicionar testemunha
          </Button>
        </div>
        {testemunhas.map((testemunha, index) => (
          <Row className="g-2 mb-2 align-items-center" key={`testemunha-${index}`}>
            <Col md={docSignAtivo ? 3 : 4}>
              <Form.Control
                placeholder={`Nome da Testemunha (${index + 1})`}
                value={testemunha.nome}
                onChange={(e) => atualizarTestemunha(index, 'nome', e.target.value)}
              />
            </Col>
            <Col md={docSignAtivo ? 2 : 3}>
              <Form.Control
                placeholder="CPF/CNPJ"
                value={testemunha.cpfCnpj}
                onChange={(e) => atualizarTestemunha(index, 'cpfCnpj', e.target.value)}
              />
            </Col>
            <Col md={docSignAtivo ? 2 : 4}>
              <Form.Control
                placeholder="Telefone"
                value={testemunha.telefone}
                onChange={(e) => atualizarTestemunha(index, 'telefone', e.target.value)}
              />
            </Col>
            {docSignAtivo && (
              <Col md={4}>
                <Form.Control
                  type="email"
                  placeholder="E-mail da testemunha"
                  value={testemunha.email || ''}
                  onChange={(e) => atualizarTestemunha(index, 'email', e.target.value)}
                />
                <Form.Check
                  className="mt-1"
                  type="checkbox"
                  label="Assina Contrato"
                  checked={Boolean(testemunha.enviaDocsign)}
                  disabled={loading}
                  onChange={(e) => atualizarTestemunha(index, 'enviaDocsign', e.target.checked)}
                />
              </Col>
            )}
            <Col md={1}>
              <Button
                size="sm"
                variant="outline-danger"
                className="w-100"
                title="Remover testemunha"
                onClick={() => removerTestemunha(index)}
                disabled={loading || testemunhas.length === 1}
              >
                <FontAwesomeIcon icon={faTrashAlt} />
              </Button>
            </Col>
          </Row>
        ))}

        <hr />
        <h6>Correspondente bancário</h6>
        <Row className="g-3">
          <Col md={6}>
            <Form.Label>Atuação geográfica</Form.Label>
            <Form.Control as="textarea" rows={3} value={atuacaoGeografica} onChange={(e) => setAtuacaoGeografica(e.target.value)} />
          </Col>
          <Col md={3}>
            <Form.Label>Quantidade de postos</Form.Label>
            <Form.Control value={quantidadePostosAtendimento} onChange={(e) => setQuantidadePostosAtendimento(e.target.value)} />
          </Col>
          <Col md={3}>
            <Form.Label>Postos de atendimento</Form.Label>
            <Form.Control as="textarea" rows={3} value={descricaoPostosAtendimento} onChange={(e) => setDescricaoPostosAtendimento(e.target.value)} />
          </Col>
        </Row>

        <Row className="g-3 mt-2">
          <Col md={6}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0">Principais convênios ativos</h6>
              <Button size="sm" variant="outline-primary" onClick={() => adicionarRelacionamento(conveniosAtivos, setConveniosAtivos)} disabled={loading}>
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Adicionar convênio
              </Button>
            </div>
            {conveniosAtivos.map((item, index) => (
              <Row className="g-2 mb-2 align-items-center" key={`convenio-${index}`}>
                <Col md={7}><Form.Control placeholder="Nome/Empresa conveniada" value={item.nome} onChange={(e) => atualizarRelacionamento(conveniosAtivos, setConveniosAtivos, index, 'nome', e.target.value)} /></Col>
                <Col md={4}><Form.Control placeholder="Tempo em meses/anos" value={item.tempo} onChange={(e) => atualizarRelacionamento(conveniosAtivos, setConveniosAtivos, index, 'tempo', e.target.value)} /></Col>
                <Col md={1}>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    className="w-100"
                    title="Remover convênio"
                    onClick={() => removerRelacionamento(conveniosAtivos, setConveniosAtivos, index)}
                    disabled={loading || conveniosAtivos.length === 1}
                  >
                    <FontAwesomeIcon icon={faTrashAlt} />
                  </Button>
                </Col>
              </Row>
            ))}
          </Col>
          <Col md={6}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0">Principais parceiros</h6>
              <Button size="sm" variant="outline-primary" onClick={() => adicionarRelacionamento(parceiros, setParceiros)} disabled={loading}>
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Adicionar parceiro
              </Button>
            </div>
            {parceiros.map((item, index) => (
              <Row className="g-2 mb-2 align-items-center" key={`parceiro-${index}`}>
                <Col md={7}><Form.Control placeholder="Nome/Empresa parceira" value={item.nome} onChange={(e) => atualizarRelacionamento(parceiros, setParceiros, index, 'nome', e.target.value)} /></Col>
                <Col md={4}><Form.Control placeholder="Tempo de parceria" value={item.tempo} onChange={(e) => atualizarRelacionamento(parceiros, setParceiros, index, 'tempo', e.target.value)} /></Col>
                <Col md={1}>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    className="w-100"
                    title="Remover parceiro"
                    onClick={() => removerRelacionamento(parceiros, setParceiros, index)}
                    disabled={loading || parceiros.length === 1}
                  >
                    <FontAwesomeIcon icon={faTrashAlt} />
                  </Button>
                </Col>
              </Row>
            ))}
          </Col>
        </Row>

        <Row className="g-3 mt-2">
          <Col md={4}>
            <Form.Label>Tempo de operação</Form.Label>
            <Form.Control value={tempoOperacao} onChange={(e) => setTempoOperacao(e.target.value)} />
          </Col>
          <Col md={8}>
            <Form.Label>Produtos em operação</Form.Label>
            <div className="d-flex flex-wrap gap-3">
              <Form.Check label="Consignado Público" checked={produtoConsignadoPublico} onChange={(e) => setProdutoConsignadoPublico(e.target.checked)} />
              <Form.Check label="Consignado Privado" checked={produtoConsignadoPrivado} onChange={(e) => setProdutoConsignadoPrivado(e.target.checked)} />
              <Form.Check label="INSS" checked={produtoInss} onChange={(e) => setProdutoInss(e.target.checked)} />
              <Form.Check label="Empréstimo Pessoal" checked={produtoEmprestimoPessoal} onChange={(e) => setProdutoEmprestimoPessoal(e.target.checked)} />
            </div>
          </Col>
        </Row>

        <hr />
        <h6>Pagamento de comissões</h6>
        <Row className="g-3">
          <Col md={4}><Form.Label>Nome do banco</Form.Label><Form.Control value={nomeBanco} onChange={(e) => setNomeBanco(e.target.value)} /></Col>
          <Col md={2}><Form.Label>Número banco</Form.Label><Form.Control value={numeroBanco} onChange={(e) => setNumeroBanco(e.target.value)} /></Col>
          <Col md={3}><Form.Label>Número agência</Form.Label><Form.Control value={numeroAgencia} onChange={(e) => setNumeroAgencia(e.target.value)} /></Col>
          <Col md={3}><Form.Label>Conta corrente</Form.Label><Form.Control value={contaCorrente} onChange={(e) => setContaCorrente(e.target.value)} /></Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        {!obrigatorio && (
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            <FontAwesomeIcon icon={faClose} className="me-2" />
            Fechar
          </Button>
        )}
        <Button id="App-button-cor-principal-escura" onClick={salvar} disabled={loading}>
          <FontAwesomeIcon icon={faSave} className="me-2" />
          Salvar dados
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default PromotoraDadosGestaoModal;
